// src/appSwitcher/index.js - the "Apps" menu in the top bar of admin, chat and the dev console (v133.57).
// Bundled by esbuild -> dist/appSwitcher.js (globalName "AppSwitcher").
//
// Matt, 6 Oct 2026: "When in dev or admin there is no easy way of switching between index, admin,
// dev, chat - can you look at adding that in the top right?" One small button per page, a menu of the
// other apps. Links open in the SAME tab, so this tab's sign-in goes with you, and the sign-in carries
// across anyway (DB.auth.resume, v133.57). The page query (a tenant hint) is kept on every link.
//
// Who sees what is decided by the Worker, not here: GET /api/admin/session says the role and whether
// the dev console is enabled for this account. Hiding an entry is a courtesy; each app still checks.

const APPS = [
  { id: 'staff', label: 'Staff app', href: 'index.html', show: () => true },
  { id: 'admin', label: 'Admin', href: 'admin.html', show: (w) => ['admin', 'area_manager', 'ho_staff'].indexOf(w.role) >= 0 },
  { id: 'chat', label: 'Messages', href: 'chat.html', show: (w) => ['admin', 'area_manager', 'ho_staff'].indexOf(w.role) >= 0 },
  { id: 'dev', label: 'Dev console', href: 'dev.html', show: (w) => w.dev_console === true },
];

// Which apps to list for this person, leaving out the page they are on. Pure: tested.
export function appsFor(who, current) {
  const w = { role: String((who && who.role) || '').toLowerCase(), dev_console: !!(who && who.dev_console === true) };
  return APPS.filter((a) => a.id !== current && a.show(w)).map((a) => ({ id: a.id, label: a.label, href: a.href }));
}

let _who = null;
let _asked = null;
function token() {
  try { return (window.DB && DB.getToken && DB.getToken()) || sessionStorage.getItem('elcAdminToken') || localStorage.getItem('elcAdminToken') || ''; } catch (e) { return ''; }
}
function whoAmI() {
  if (_who) return Promise.resolve(_who);
  if (_asked) return _asked;
  const tk = token();
  const base = (window.DB && DB.workerUrl) ? DB.workerUrl() : '';
  if (!tk) return Promise.resolve(null);
  _asked = fetch(base + '/api/admin/session', { headers: { Authorization: 'Bearer ' + tk }, cache: 'no-store' })
    .then((r) => (r.ok ? r.json() : null)).then((d) => { _who = d && d.ok ? d : null; _asked = null; return _who; })
    .catch(() => { _asked = null; return null; });
  return _asked;
}

function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]); }

// Puts the button into `host` (an element or its id). `current`: 'admin' | 'chat' | 'dev'.
// `className`: the page's own button class, so it matches its neighbours.
export function mount(host, current, className) {
  const el = typeof host === 'string' ? document.getElementById(host) : host;
  if (!el || el.querySelector('.app-switch')) return;
  const wrap = document.createElement('span');
  wrap.className = 'app-switch';
  wrap.style.cssText = 'position:relative;display:inline-block';
  wrap.innerHTML = '<button type="button" class="' + esc(className || '') + '" aria-haspopup="true" aria-expanded="false" title="Switch app">Apps &#9662;</button>'
    + '<div role="menu" hidden style="position:absolute;right:0;top:calc(100% + 6px);min-width:170px;background:var(--card,#fff);color:var(--text,#111);border:1px solid rgba(0,0,0,.12);border-radius:10px;box-shadow:0 10px 28px rgba(0,0,0,.18);padding:6px;z-index:9999;text-align:start"></div>';
  const btn = wrap.querySelector('button');
  const menu = wrap.querySelector('[role="menu"]');
  const close = () => { menu.hidden = true; btn.setAttribute('aria-expanded', 'false'); };
  const fill = (who) => {
    // v133.86 (sweep L): carry the address's settings across, but never a one-time ?screen= link
    const sp = new URLSearchParams(location.search || ''); sp.delete('screen');
    sp.delete('rotastore');   // v136.3: the Store rotas link is one-time too
    const q = sp.toString() ? '?' + sp.toString() : '';
    const items = appsFor(who || {}, current);
    menu.innerHTML = items.map((a) => '<a role="menuitem" href="' + esc(a.href + q) + '" style="display:block;padding:9px 12px;border-radius:7px;color:var(--text,#111);text-decoration:none;font:600 13px system-ui,sans-serif">' + esc(a.label) + '</a>').join('')
      || '<div style="padding:9px 12px;font:13px system-ui,sans-serif;color:var(--tiny,#666)">No other apps for this account.</div>';
  };
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (!menu.hidden) { close(); return; }
    fill(_who);   // what is known now; refined when the Worker answers
    menu.hidden = false; btn.setAttribute('aria-expanded', 'true');
    whoAmI().then((w) => { if (!menu.hidden) fill(w); });
  });
  document.addEventListener('click', (e) => { if (!wrap.contains(e.target)) close(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  el.appendChild(wrap);
}
